import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Spinner, Alert, Card, Badge } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCalendarAlt, FaBoxOpen, FaHourglassHalf, FaTruck, FaClipboardCheck } from "react-icons/fa";
import { bookingsAPI } from '../../api/apiClient';
import { useTranslation } from "react-i18next";
import "../../assets/css/TrackBooking.css";

export default function TrackBooking() {
  const { t } = useTranslation();
  const { booking_id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trackingData, setTrackingData] = useState(null);

  useEffect(() => {
    fetchTrackingData();
  }, [booking_id]);

  const fetchTrackingData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await bookingsAPI.track(booking_id);
      
      if (response.success) {
        setTrackingData(response.data.data || response.data);
      } else {
        setError(response.message || t('failed_to_fetch_tracking'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_tracking'));
      console.error('Error fetching tracking data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getStepIcon = (stepKey) => {
    const icons = {
      created: <FaCalendarAlt size={24} />,
      approved: <FaBoxOpen size={24} />,
      assigned: <FaHourglassHalf size={24} />,
      picked_up: <FaTruck size={24} />,
      delivered: <FaClipboardCheck size={24} />
    };
    return icons[stepKey] || <FaCalendarAlt size={24} />;
  };

  const getStepTitle = (stepKey, stepData) => {
    const titles = {
      created: t('order_created'),
      approved: t('order_approved'),
      assigned: t('order_assigned'),
      picked_up: t('order_picked_up'),
      delivered: t('order_delivered')
    };
    return stepData?.title || titles[stepKey] || stepKey;
  };

  const formatDate = (dateString) => {
    if (!dateString) return { date: '', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" />
        <span className="ms-2">{t('loading')}</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="p-4">
        <Alert variant="danger">
          {error}
          <div className="mt-3">
            <Button variant="outline-danger" onClick={handleBack}>
              <FaArrowLeft className="me-2" />
              {t('back')}
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!trackingData) {
    return (
      <Container className="p-4">
        <Alert variant="warning">
          {t('no_tracking_data')}
          <div className="mt-3">
            <Button variant="outline-warning" onClick={handleBack}>
              <FaArrowLeft className="me-2" />
              {t('back')}
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  const steps = Object.entries(trackingData.tracking_stages || {}).map(([key, data], index, array) => {
    const isActive = !!data?.status && data.status !== null;
    
    let lastActiveIndex = -1;
    for (let i = array.length - 1; i >= 0; i--) {
      if (!!array[i][1]?.status && array[i][1].status !== null) {
        lastActiveIndex = i;
        break;
      }
    }
    
    const isCurrent = !isActive && index === lastActiveIndex + 1;
    
    const dateInfo = data?.date ? formatDate(data.date) : { date: '', time: '' };
    
    return {
      key,
      title: getStepTitle(key, data),
      desc: data?.details || '',
      location: data?.location || '',
      date: dateInfo.date,
      time: dateInfo.time,
      icon: getStepIcon(key),
      active: isActive,
      current: isCurrent,
      status: data?.status
    };
  });

  return (
    <Container className="shipment-container p-4">
      {/* Header */}
      <Card className="tracking-header border-0 shadow">
        <Card.Body className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h4 className="mb-1">
              {t('booking_tracking')}: <span className="text-warning">{trackingData.booking_id}</span>
            </h4>
            <p className="mb-0 opacity-90">{t('tracking_details')}</p>
          </div>
          <Button className="back-btn" onClick={handleBack}>
            <FaArrowLeft className="me-2" />
            {t('back_to_details')}
          </Button>
        </Card.Body>
      </Card>

      {/* Horizontal Timeline */}
      <div className="timeline-horizontal">
        {steps.map((step, index) => (
          <div 
            key={step.key} 
            className={`timeline-item ${step.current ? 'current-item' : ''} ${step.active ? 'active' : ''}`}
          >
            {index < steps.length - 1 && (
              <div 
                className={`timeline-connector ${step.active ? 'active' : ''}`}
              ></div>
            )}
            
            <div className={`timeline-icon ${step.current ? 'current' : step.active ? 'active' : 'inactive'}`}>
              {step.icon}
            </div>
            
            <div className="timeline-content">
              <h5 className={`timeline-title ${step.current ? 'current' : step.active ? 'active' : 'inactive'}`}>
                {t(step.title)}
              </h5>
              
              <p className="timeline-desc">{t(step.desc)}</p>
              
              {step.location && (
                <p className="timeline-location">{step.location}</p>
              )}
              
              {step.date && (
                <div className="timeline-date-time">
                  <div className="timeline-date">{step.date}</div>
                  {step.time && (
                    <div className="timeline-time">{step.time}</div>
                  )}
                </div>
              )}
              
              {step.status && (
                <Badge 
                  bg={step.active ? 'success' : 'secondary'} 
                  className="timeline-badge"
                >
                  {t(step.status)}
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </Container>
  );
}